import { useCallback } from 'react';
import { LayoutGrid, List } from 'lucide-react';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { cn } from '@/lib/utils';

export type CatalogueView = 'card' | 'list';

const STORAGE_KEY = 'slc.catalogue.view';

function parseView(raw: string): CatalogueView | null {
  return raw === 'card' || raw === 'list' ? raw : null;
}

/** Persisted so a user's preferred density survives navigation and refreshes. */
export function useCatalogueView(): [CatalogueView, (view: CatalogueView) => void] {
  const parse = useCallback(parseView, []);
  return useLocalStorage<CatalogueView>(STORAGE_KEY, 'card', parse);
}

export function ViewToggle({
  view,
  onChange,
}: {
  view: CatalogueView;
  onChange: (view: CatalogueView) => void;
}) {
  const options: ReadonlyArray<{ value: CatalogueView; label: string; Icon: typeof List }> = [
    { value: 'card', label: 'Card view', Icon: LayoutGrid },
    { value: 'list', label: 'List view', Icon: List },
  ];

  return (
    <div
      role="group"
      aria-label="Catalogue layout"
      className="inline-flex items-center rounded-md border border-input p-0.5"
    >
      {options.map(({ value, label, Icon }) => (
        <button
          key={value}
          type="button"
          aria-pressed={view === value}
          onClick={() => onChange(value)}
          title={label}
          className={cn(
            'inline-flex h-8 w-8 items-center justify-center rounded-sm transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            view === value
              ? 'bg-secondary text-secondary-foreground'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <Icon aria-hidden="true" className="h-4 w-4" />
          <span className="sr-only">{label}</span>
        </button>
      ))}
    </div>
  );
}
